import * as React from 'react';
import { 
  Box,
  Alert,
  CircularProgress,
  DialogContent,
  IconButton,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Typography
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { CDialog, CDialogTitle, CDialogActions, CButton, CTextField } from '../../../components/common/Modal';
import { createSource, updateSource, getOracleEpmConfigBySource, getCubesBySource } from '../../../slices/source';

// Import custom hooks
import { useConnectionTest } from './hooks/useConnectionTest';
import { useSyncMetadata } from './hooks/useSyncMetadata';
import { useCubeManagement } from './hooks/useCubeManagement';

// Import wizard components
import { OracleEPMWizard } from './components/OracleEPMWizard';
import { RegularDatabaseWizard } from './components/RegularDatabaseWizard';
import { AdminCredentialsSection } from './components/AdminCredentialsSection';

export default function SourceModal(props) {
  const {open, mode, handleClose} = props;
  const {
    id: originId, 
    name: originSource, 
    type: originType,
    hostname: originHostname,
    username: originUserName, 
    password: originPassword, 
    database: originDatabase, 
    port: originPort
  } = useSelector(state => state.source.currentSource);

  const dispatch = useDispatch();
  
  // Form state
  const [loading, setLoading] = React.useState(false);
  const [source, setSource] = React.useState("");
  const [type, setType] = React.useState("");
  const [hostname, setHostname] = React.useState("");
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [database, setDatabase] = React.useState("");
  const [port, setPort] = React.useState("");
  const [isAdminCredential, setIsAdminCredential] = React.useState(true);
  
  // Oracle EPM specific state
  const [version, setVersion] = React.useState("");
  const [applicationName, setApplicationName] = React.useState("");
  const [metadataExportName, setMetadataExportName] = React.useState("");
  const [retryInterval, setRetryInterval] = React.useState("");
  const [maxRetries, setMaxRetries] = React.useState("");
  const [loadingEpmData, setLoadingEpmData] = React.useState(false);
  
  // Custom hooks
  const connectionTest = useConnectionTest();
  const syncMetadata = useSyncMetadata();
  const cubeManagement = useCubeManagement();
  
  const isMountedRef = React.useRef(true);

  // Load Oracle EPM data for edit mode
  const loadOracleEpmData = async (sourceId) => {
    if (!sourceId || originType !== 'OracleEPM' || loadingEpmData || !isMountedRef.current) return;
    
    setLoadingEpmData(true);
    try {
      const [epmConfigResult, cubesResult] = await Promise.all([
        dispatch(getOracleEpmConfigBySource(sourceId)).unwrap(),
        dispatch(getCubesBySource({ sourceId })).unwrap()
      ]);

      if (!isMountedRef.current) return;

      if (epmConfigResult) {
        setVersion(epmConfigResult.version || "");
        setApplicationName(epmConfigResult.application_name || "");
        setMetadataExportName(epmConfigResult.metadata_export_name || "");
        setRetryInterval(epmConfigResult.retry_interval?.toString() || "");
        setMaxRetries(epmConfigResult.max_retries?.toString() || "");
      }

      if (cubesResult && cubesResult.items) {
        cubeManagement.setLoadedCubes(cubesResult.items);
        const formattedCubes = cubesResult.items.map(cube => ({
          id: cube.id,
          name: cube.cube_name,
          dimensions: cube.dimensions ? cube.dimensions.map(dim => dim.dimension || dim.dimension_name || 'Unknown Dimension') : []
        }));
        cubeManagement.setCubes(formattedCubes);

        const allDimensions = new Set();
        cubesResult.items.forEach(cube => {
          if (cube.dimensions) {
            cube.dimensions.forEach(dim => {
              const dimensionName = dim.dimension || dim.dimension_name || 'Unknown Dimension';
              allDimensions.add(dimensionName);
            });
          }
        });
        syncMetadata.setAvailableDimensions(Array.from(allDimensions));
      }

    } catch (error) {
      console.error('Error loading Oracle EPM data:', error);
    } finally {
      if (isMountedRef.current) {
        setLoadingEpmData(false);
      }
    }
  };

  // Initialize form when modal opens
  React.useEffect(() => {
    if (!open) return;

    if(mode === "Create") {
      setSource("");
      setType("");
      setHostname("");
      setDatabase("");
      setPort("");
      setUsername("");
      setPassword("");
      setVersion("");
      setApplicationName("");
      setMetadataExportName("");
      setRetryInterval("");
      setMaxRetries("");
      connectionTest.clearTestResult();
      syncMetadata.clearSyncResult();
      syncMetadata.setAvailableDimensions([]);
      cubeManagement.setCubes([]);
      cubeManagement.setLoadedCubes([]);
      setLoadingEpmData(false);
    } else {
      setSource(originSource);
      setType(originType);
      setHostname(originHostname);
      setDatabase(originDatabase);
      setPort(originPort);
      setUsername(originUserName);
      setPassword(originPassword);
      
      if (originType === 'OracleEPM' && originId && open) {
        loadOracleEpmData(originId);
      }
    }
    connectionTest.clearTestResult();
  }, [mode, open, originId, originType]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Clear test result when connection details change
  React.useEffect(() => {
    connectionTest.clearTestResult();
    syncMetadata.clearSyncResult();
  }, [hostname, username, password, type]);

  const handleIsAdminCredential = (e) => {
    setIsAdminCredential(e.target.checked);
  };

  const handleSave = (e) => {
    setLoading(true);
    let formData = new FormData();
    formData.append('name', source);
    formData.append('type', type);
    formData.append('hostname', hostname);
    formData.append('username', username);
    formData.append('password', password);
    formData.append('database', database);
    formData.append('port', port);
    formData.append('is_admin_credential', isAdminCredential);

    const action = mode === "Edit" ? updateSource({ sourceId: originId, sourceInfo: formData }) : createSource(formData);
    
    dispatch(action)
      .unwrap()
      .then(res => {
        setLoading(false);
        const message = mode === "Edit" ? "Source updated successfully!" : "Create new source successfully!";
        window.alert(message);
        
        if (mode === "Create") {
          setSource("");
          setType("");
          setHostname("");
          setUsername("");
          setPassword("");
          setDatabase("");
          setPort("");
        }
        handleClose();
      })
      .catch(err => {
        console.log(err);
        setLoading(false);
      });
  };

  return (
    <CDialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <CDialogTitle>{mode === "Edit" ? "Edit Data Source" : "New Data Source"}</CDialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 6,
          top: 6,
          color: (theme) => theme.palette.grey[800],
        }}
      >
        <CloseIcon fontSize='small' />
      </IconButton>
      <DialogContent>
        {/* Source Name and Type - Always visible at the top */}
        <CTextField
          id="sourcename" 
          label="Source Name"
          size="small"
          variant="outlined"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          fullWidth
        />
        <FormControl sx={{ mt: '1rem', minWidth: 120 }} size="small" fullWidth>
          <InputLabel id="demo-select-small-label">Type</InputLabel>
          <Select
            labelId="demo-select-small-label"
            id="demo-select-small"
            value={type}
            label="Type"
            onChange={(e) => setType(e.target.value)}
          >
            <MenuItem value={"SQLite"}>SQLite</MenuItem>
            <MenuItem value={"Oracle"}>Oracle</MenuItem>
            <MenuItem value={"OracleEPM"}>Oracle EPM</MenuItem>
            <MenuItem value={"SQLServer"}>SQLServer</MenuItem>
            <MenuItem value={"PostgreSQL"}>PostgreSQL</MenuItem>
            <MenuItem value={"MySQL"}>MySQL</MenuItem>
          </Select>
        </FormControl>

        {/* Wizard UI for Oracle EPM */}
        {type === 'OracleEPM' ? (
          <OracleEPMWizard
            mode={mode}
            originId={originId}
            source={source}
            hostname={hostname}
            username={username}
            password={password}
            isAdminCredential={isAdminCredential}
            version={version}
            applicationName={applicationName}
            metadataExportName={metadataExportName}
            retryInterval={retryInterval}
            maxRetries={maxRetries}
            loadingEpmData={loadingEpmData}
            onHostnameChange={(e) => setHostname(e.target.value)}
            onUsernameChange={(e) => setUsername(e.target.value)}
            onPasswordChange={(e) => setPassword(e.target.value)}
            onAdminCredentialChange={handleIsAdminCredential}
            onVersionChange={(e) => setVersion(e.target.value)}
            onApplicationNameChange={(e) => setApplicationName(e.target.value)}
            onMetadataExportNameChange={(e) => setMetadataExportName(e.target.value)}
            onRetryIntervalChange={(e) => setRetryInterval(e.target.value)}
            onMaxRetriesChange={(e) => setMaxRetries(e.target.value)}
            connectionTest={connectionTest}
            syncMetadata={syncMetadata}
            cubeManagement={cubeManagement}
            onClose={handleClose}
          />
        ) : type && type !== 'SQLite' ? (
          // Wizard UI for other database types
          <RegularDatabaseWizard
            type={type}
            hostname={hostname}
            database={database}
            port={port}
            username={username}
            password={password}
            isAdminCredential={isAdminCredential}
            onHostnameChange={(e) => setHostname(e.target.value)}
            onDatabaseChange={(e) => setDatabase(e.target.value)}
            onPortChange={(e) => setPort(e.target.value)}
            onUsernameChange={(e) => setUsername(e.target.value)}
            onPasswordChange={(e) => setPassword(e.target.value)}
            onAdminCredentialChange={handleIsAdminCredential}
            connectionTest={connectionTest}
            onFinish={handleSave}
          />
        ) : (
          // Regular UI for SQLite (no wizard needed)
          <>
            {!(type === 'SQLite' || type === 'Oracle' || type === 'OracleEPM') && (
              <>
                <CTextField
                  id="database" 
                  label="Database"
                  size="small"
                  variant="outlined"
                  value={database}
                  onChange={(e) => setDatabase(e.target.value)}
                  sx={{ marginTop: '1rem' }}
                  fullWidth
                />
                <CTextField
                  id="port" 
                  label="Port"
                  size="small"
                  variant="outlined"
                  value={port}
                  onChange={(e) => setPort(e.target.value)}
                  sx={{ marginTop: '1rem' }}
                  fullWidth
                />
              </>
            )}
            
            <AdminCredentialsSection
              isAdminCredential={isAdminCredential}
              username={username}
              password={password}
              onAdminCredentialChange={handleIsAdminCredential}
              onUsernameChange={(e) => setUsername(e.target.value)}
              onPasswordChange={(e) => setPassword(e.target.value)}
            />
            
            {!isAdminCredential && (
              <Alert severity="info" sx={{ marginTop: '1rem' }}>
                This database requires user credentials. Users will need to provide their own database credentials when connecting.
              </Alert>
            )}
            
            {isAdminCredential && (
              <Alert severity="success" sx={{ marginTop: '1rem' }}>
                Admin credentials will be used for all users. No additional authentication required.
              </Alert>
            )}
            
            {loading && (
              <Box className="absolute w-full h-full" sx={{top: 0, left: 0}}>
                <Box className="relative w-full h-full">
                  <Box className="w-full h-full flex justify-center align-center">
                    <CircularProgress sx={{opacity: 0.7}} />
                  </Box>
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <CDialogActions>
        {type && type !== 'SQLite' && (type === 'OracleEPM' || (type !== 'OracleEPM' && type)) ? (
          // Wizard handles its own navigation buttons, so just show Cancel
          <CButton onClick={handleClose}>Cancel</CButton>
        ) : (
          // Regular buttons for SQLite
          <>
            <CButton onClick={handleClose}>Cancel</CButton>
            <CButton onClick={handleSave}>Ok</CButton>
          </>
        )}
      </CDialogActions>
    </CDialog>
  );
}

